import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  Row,
  Spinner,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from 'reactstrap';
import { connect } from 'react-redux';
import { ChromePicker } from 'react-color';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';
import ImageGallery from '../../../../../../views/app/pages/propinfo/gallery/index';
import IconGallery from '../../../../../../views/app/pages/propinfo/icon-gallery/index';
import { setBackGroundColor } from '../../../../../../views/app/pages/integration/style/DigitalAssistantStyle';

const DEFAULT_APPEARANCE = {
  iconBackgroundColor: '#4A89DC',
  iconColor: '#FFF',
};

const ITEM_TYPE_OPTIONS = [
  { title: 'Task', value: 'task' },
  { title: 'Repair', value: 'repair' },
  { title: 'Room order', value: 'room_order' },
  { title: 'Flaw cleaning', value: 'flaw_cleaning' },
  { title: 'Issue', value: 'issue' },
  { title: 'Feedback', value: 'feedback' },
];

function WorkOrderEditModal({
  item,
  isOpen,
  toggle,
  onSave,
  saving,
  propertyId,
  langCode,
}) {
  const [form, setForm] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [iconGalleryOpen, setIconGalleryOpen] = useState(false);
  const [colorPicker, setColorPicker] = useState({ bg: false, icon: false });

  // Seed form whenever item changes
  useEffect(() => {
    if (!item || !langCode) return;
    setForm({
      name:
        item.name?.find((n) => n.languageCode === langCode)?.statement ||
        item.name?.find((n) => n.languageCode === 'en')?.statement ||
        '',
      type: item.type || '',
      code: item.code || '',
      facilityId: item.facilityId || '',
      imageLink: item.imageLink || '',
      iconLink: item.iconLink || '',
      source: item.source || '',
      price: item.price != null ? String(item.price) : '',
      appearance: item.appearance || DEFAULT_APPEARANCE,
    });
  }, [item, langCode]);

  if (!form) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAppearance = (key, value) => {
    setForm((prev) => ({
      ...prev,
      appearance: { ...prev.appearance, [key]: value },
    }));
  };

  const handleSave = () => {
    // Build the payload merging changes back onto the original item
    const updated = {
      ...item,
      name: [
        { statement: form.name, languageCode: langCode ?? 'en' },
        ...(item.name || []).filter((n) => n.languageCode !== langCode),
      ],
      type: form.type,
      code: form.code,
      facilityId: form.facilityId,
      imageLink: form.imageLink || null,
      iconLink: form.iconLink || null,
      source: form.source || null,
      price: form.price !== '' ? Number(form.price) : null,
      appearance: form.appearance,
    };
    onSave(updated);
  };

  const appearance = form.appearance || DEFAULT_APPEARANCE;

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
        <ModalHeader toggle={toggle}>
          <b>Edit Work Order</b>
        </ModalHeader>

        <ModalBody>
          <Row>
            {/* ── Left column ─────────────────────────── */}
            <Colxx xxs="12" md="6">
              {/* Name */}
              <Label className="mt-2">
                <b>Name</b>
              </Label>
              <Input
                type="text"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Make-up room"
              />

              {/* Type */}
              <Label className="mt-3">
                <b>Item Type</b>
              </Label>
              <Input
                type="select"
                value={form.type}
                onChange={(e) => handleChange('type', e.target.value)}
              >
                {ITEM_TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.title}
                  </option>
                ))}
              </Input>

              {/* Facility ID */}
              <Label className="mt-3">
                <b>Facility ID</b>
              </Label>
              <Input
                type="text"
                value={form.facilityId}
                onChange={(e) => handleChange('facilityId', e.target.value)}
                placeholder="e.g. 1"
                readOnly={true}
              />

              {/* External Item ID (code) */}
              <Label className="mt-3">
                <b>External Item ID</b>
              </Label>
              <Input
                type="text"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value)}
                placeholder="e.g. 166"
                readOnly={true}
              />

              {/* Source */}
              <Label className="mt-3">
                <b>Source</b>
              </Label>
              <Input
                type="text"
                value={form.source}
                onChange={(e) => handleChange('source', e.target.value)}
                placeholder="e.g. Flexkeeping"
                readOnly={true}
              />

              {/* Price */}
              <Label className="mt-3">
                <b>Price ($)</b>
              </Label>
              <Input
                type="number"
                min="0"
                value={form.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="0"
                readOnly={true}
              />
            </Colxx>

            {/* ── Right column ────────────────────────── */}
            <Colxx xxs="12" md="6">
              {/* Image */}
              <Label className="mt-2">
                <b>Image</b>
              </Label>
              <div className="d-flex align-items-center mb-1">
                <Button
                  size="xs"
                  color="primary"
                  outline
                  onClick={() => setGalleryOpen(true)}
                  style={{ borderRadius: '20px' }}
                >
                  Select Image
                </Button>
                {form.imageLink && (
                  <Button
                    size="xs"
                    color="danger"
                    outline
                    className="ml-2"
                    style={{ borderRadius: '20px' }}
                    onClick={() => handleChange('imageLink', '')}
                  >
                    Remove
                  </Button>
                )}
              </div>
              {form.imageLink ? (
                <img
                  src={form.imageLink}
                  alt="Work order"
                  style={{
                    width: '100%',
                    maxHeight: '140px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100px',
                    border: '2px dashed #dee2e6',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#aaa',
                    fontSize: '13px',
                  }}
                >
                  No image selected
                </div>
              )}

              {/* Icon */}
              <Label className="mt-3">
                <b>Icon</b>
              </Label>
              <div className="d-flex align-items-center mb-1">
                <Button
                  size="xs"
                  color="primary"
                  outline
                  onClick={() => setIconGalleryOpen(true)}
                  style={{ borderRadius: '20px' }}
                >
                  Select Icon
                </Button>
                {form.iconLink && (
                  <Button
                    size="xs"
                    color="danger"
                    outline
                    className="ml-2"
                    style={{ borderRadius: '20px' }}
                    onClick={() => handleChange('iconLink', '')}
                  >
                    Remove
                  </Button>
                )}
              </div>
              {form.iconLink ? (
                <img
                  src={form.iconLink}
                  alt="Icon"
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    padding: '4px',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    border: '2px dashed #dee2e6',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#aaa',
                    fontSize: '11px',
                  }}
                >
                  None
                </div>
              )}

              {/* Appearance ─ Primary Color */}
              {/* <Label className="mt-3">
                <b>Appearance</b>
              </Label> */}

              {/* <div className="mb-2">
                <span className="text-muted" style={{ fontSize: '12px' }}>
                  Primary Color
                </span>
                <InputGroup>
                  <Input
                    type="text"
                    value={appearance.iconBackgroundColor || ''}
                    onChange={(e) =>
                      handleAppearance('iconBackgroundColor', e.target.value)
                    }
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText
                      className="color-square"
                      style={{
                        ...setBackGroundColor(appearance.iconBackgroundColor),
                        cursor: 'pointer',
                        width: '36px',
                      }}
                      onClick={() =>
                        setColorPicker((p) => ({ ...p, bg: !p.bg }))
                      }
                    />
                  </InputGroupAddon>
                </InputGroup>
                {colorPicker.bg && (
                  <div
                    className="parent-div"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="cover">
                      <ChromePicker
                        disableAlpha
                        color={appearance.iconBackgroundColor}
                        onChangeComplete={(c) => {
                          handleAppearance('iconBackgroundColor', c.hex);
                          setColorPicker((p) => ({ ...p, bg: false }));
                        }}
                      />
                    </div>
                  </div>
                )}
              </div> */}

              {/* Appearance ─ Icon Color */}
              {/* <div className="mt-2">
                <span className="text-muted" style={{ fontSize: '12px' }}>
                  Icon Color
                </span>
                <InputGroup>
                  <Input
                    type="text"
                    value={appearance.iconColor || ''}
                    onChange={(e) =>
                      handleAppearance('iconColor', e.target.value)
                    }
                  />
                  <InputGroupAddon addonType="append">
                    <InputGroupText
                      className="color-square"
                      style={{
                        ...setBackGroundColor(appearance.iconColor),
                        cursor: 'pointer',
                        width: '36px',
                      }}
                      onClick={() =>
                        setColorPicker((p) => ({ ...p, icon: !p.icon }))
                      }
                    />
                  </InputGroupAddon>
                </InputGroup>
                {colorPicker.icon && (
                  <div
                    className="parent-div"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="cover">
                      <ChromePicker
                        disableAlpha
                        color={appearance.iconColor}
                        onChangeComplete={(c) => {
                          handleAppearance('iconColor', c.hex);
                          setColorPicker((p) => ({ ...p, icon: false }));
                        }}
                      />
                    </div>
                  </div>
                )}
              </div> */}
            </Colxx>
          </Row>
        </ModalBody>

        <ModalFooter>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="mr-1" /> Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
          <Button color="secondary" outline onClick={toggle} disabled={saving}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Image Gallery Modal */}
      {galleryOpen && (
        <Modal
          isOpen={galleryOpen}
          toggle={() => setGalleryOpen(false)}
          size="xl"
        >
          <ModalHeader toggle={() => setGalleryOpen(false)}>
            Select Image
          </ModalHeader>
          <ModalBody>
            <ImageGallery
              propertyId={propertyId}
              togglegallery={() => setGalleryOpen(false)}
              selectedImage={(img) => {
                handleChange('imageLink', img?.src || img?.url || '');
                setGalleryOpen(false);
              }}
            />
          </ModalBody>
        </Modal>
      )}

      {/* Icon Gallery Modal */}
      {iconGalleryOpen && (
        <Modal
          isOpen={iconGalleryOpen}
          toggle={() => setIconGalleryOpen(false)}
          size="xl"
        >
          <ModalHeader toggle={() => setIconGalleryOpen(false)}>
            Select Icon
          </ModalHeader>
          <ModalBody>
            <IconGallery
              propertyId={propertyId}
              togglegallery={() => setIconGalleryOpen(false)}
              selectedSvg={(img) => {
                handleChange('iconLink', img?.src || img?.url || '');
                setIconGalleryOpen(false);
              }}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
}

const mapStateToProps = ({ settings }) => {
  const { propertyId } = settings;
  return { propertyId };
};

export default connect(mapStateToProps, {})(WorkOrderEditModal);
